import React, { useState, useEffect } from 'react';
import { FaTimes, FaUpload } from 'react-icons/fa';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../../../firebase/config';
import './AdvisorModal.scss';

const AdvisorModal = ({ advisor, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    designation: '',
    organogram: 'adviser',
    serial: 1,
    img: '',
    publicDescription: '',
    shortDescription: '',
    shortProfileBrief: '',
    socialLinkedIn: '',
    socialTwitter: '',
    socialInstagram: '',
    socialWeb: ''
  });
  
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (advisor) {
      setFormData({
        name: advisor.name || '',
        designation: advisor.designation || '',
        organogram: advisor.organogram || 'adviser',
        serial: advisor.serial || 1,
        img: advisor.img || '',
        publicDescription: advisor.publicDescription || '',
        shortDescription: advisor.shortDescription || '',
        shortProfileBrief: advisor.shortProfileBrief || '',
        socialLinkedIn: advisor.socialLinkedIn || '',
        socialTwitter: advisor.socialTwitter || '',
        socialInstagram: advisor.socialInstagram || '',
        socialWeb: advisor.socialWeb || ''
      });
      setImagePreview(advisor.img || '');
    }
  }, [advisor]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleUploadImage = async () => {
    if (!imageFile) return formData.img;
    
    try {
      setUploading(true);
      const timestamp = Date.now();
      const fileName = `advisor-${timestamp}-${imageFile.name}`;
      const storageRef = ref(storage, `advisors/${fileName}`);
      
      await uploadBytes(storageRef, imageFile);
      const downloadURL = await getDownloadURL(storageRef);
      
      setUploading(false);
      return downloadURL;
    } catch (error) {
      console.error('Error uploading image:', error);
      setUploading(false);
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.name.trim() || !formData.designation.trim()) {
      alert('Name and designation are required.');
      return;
    }
    
    try {
      setSaving(true);
      
      // Upload image if there's a new one
      const imageUrl = await handleUploadImage();
      
      const advisorData = {
        ...formData,
        img: imageUrl,
        serial: parseInt(formData.serial) || 1
      };
      
      await onSave(advisorData);
      setSaving(false);
    } catch (error) {
      console.error('Error saving advisor:', error);
      alert('Failed to save advisor. Please try again.');
      setSaving(false);
    }
  };

  return (
    <div className="advisor-modal-overlay">
      <div className="advisor-modal">
        <div className="modal-header">
          <h2>{advisor ? 'Edit Advisor' : 'Add New Advisor'}</h2>
          <button className="close-button" onClick={onClose}>
            <FaTimes />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="advisor-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="name">Name *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="Enter advisor name"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="serial">Serial Number *</label>
              <input
                type="number"
                id="serial"
                name="serial"
                value={formData.serial}
                onChange={handleChange}
                required
                min="1"
                placeholder="Enter serial number"
              />
            </div>
          </div>
          
          <div className="form-group">
            <label htmlFor="designation">Designation *</label>
            <input
              type="text"
              id="designation"
              name="designation"
              value={formData.designation}
              onChange={handleChange}
              required
              placeholder="Enter designation"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="organogram">Organogram</label>
            <select
              id="organogram"
              name="organogram"
              value={formData.organogram}
              onChange={handleChange}
            >
              <option value="adviser">Adviser</option>
              <option value="board">Board Member</option>
              <option value="mentor">Mentor</option>
              <option value="consultant">Consultant</option>
            </select>
          </div>
          
          <div className="form-group">
            <label htmlFor="image">Profile Image</label>
            <div className="image-upload-container">
              {imagePreview && (
                <div className="image-preview">
                  <img src={imagePreview} alt="Preview" />
                </div>
              )}
              <label className="custom-file-upload">
                <input
                  type="file"
                  id="image"
                  accept="image/*"
                  onChange={handleImageChange}
                />
                <FaUpload /> Choose Image
              </label>
            </div>
          </div>
          
          <div className="form-group">
            <label htmlFor="shortProfileBrief">Short Profile Brief</label>
            <textarea
              id="shortProfileBrief"
              name="shortProfileBrief"
              value={formData.shortProfileBrief}
              onChange={handleChange}
              placeholder="Brief profile summary"
              rows="2"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="shortDescription">Short Description</label>
            <textarea
              id="shortDescription"
              name="shortDescription"
              value={formData.shortDescription}
              onChange={handleChange}
              placeholder="Enter short description"
              rows="4"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="publicDescription">Public Description</label>
            <textarea
              id="publicDescription"
              name="publicDescription"
              value={formData.publicDescription}
              onChange={handleChange}
              placeholder="Enter detailed public description"
              rows="6"
            />
          </div>
          
          <div className="social-section">
            <h3>Social Media Links</h3>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="socialLinkedIn">LinkedIn</label>
                <input
                  type="url"
                  id="socialLinkedIn"
                  name="socialLinkedIn"
                  value={formData.socialLinkedIn}
                  onChange={handleChange}
                  placeholder="https://linkedin.com/in/..."
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="socialTwitter">Twitter</label>
                <input
                  type="url"
                  id="socialTwitter"
                  name="socialTwitter"
                  value={formData.socialTwitter}
                  onChange={handleChange}
                  placeholder="https://twitter.com/..."
                />
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="socialInstagram">Instagram</label>
                <input
                  type="url"
                  id="socialInstagram"
                  name="socialInstagram"
                  value={formData.socialInstagram}
                  onChange={handleChange}
                  placeholder="https://instagram.com/..."
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="socialWeb">Website</label>
                <input
                  type="url"
                  id="socialWeb"
                  name="socialWeb"
                  value={formData.socialWeb}
                  onChange={handleChange}
                  placeholder="https://..."
                />
              </div>
            </div>
          </div>
          
          <div className="form-actions">
            <button type="button" className="cancel-button" onClick={onClose}>
              Cancel
            </button>
            <button 
              type="submit" 
              className="submit-button" 
              disabled={saving || uploading}
            >
              {saving ? 'Saving...' : uploading ? 'Uploading...' : (advisor ? 'Update Advisor' : 'Add Advisor')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdvisorModal; 